import * as React from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Formik } from "formik";
import { LocalUser } from "@preveil-api";
import { AppConfiguration, PublicRoutes, QueryParamKeys, QueryParamValues, StatusCode } from "src/common";
import { Message } from "src/common/app/message-item.class";
import { MessageHandlerDisplayType } from "src/common/constants/constants";
import { DeveloperErrorMessages, DeveloperSuccessMessages, DeveloperWarningMessages, useAddOrgUser } from "src/common/developers";
import { uiActions } from "src/store";
import * as yup from "yup";

type AllProps = {
  admin_user: LocalUser;
}

function AddNewUserFormComponent(props: AllProps) {
  const { admin_user } = props;
  const default_user_id = "standard_@testorg.com".replace(/@/, self.crypto.randomUUID().slice(0, 4) + "@");
  const default_display_name = "Standard New";
  const default_department = "Dept";
  const admin_id = admin_user.user_id;
  const org_id = admin_user.org_info?.org_id || "";
  const dispatch = useDispatch();
  const { success, addNewUsers, setSuccess } = useAddOrgUser();

  const schema = yup.object().shape({
    user_id: yup.string().email("Invalid email").required("User ID is required"),
    display_name: yup.string().required("Display name is required"),
    department: yup.string().required("Department is required"),
    user_number: yup
      .number()
      .min(1, "Invalid number. Please pick a number between 1 and 200")
      .max(100, "Invalid number. Please pick a number between 1 and 200")
      .required("Number of users is required"),
  });

  function handleError(error: any) {
    switch (error.status) {
      case StatusCode.NO_EMAIL_SIGNATURE_CODE:
        if (error.data.errors[0].status === "not_found") {
          dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_user_already_exists, MessageHandlerDisplayType.toastr)));
        } else {
          dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_API_error, MessageHandlerDisplayType.toastr)));
        }
        break;
      default:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_creating_user, MessageHandlerDisplayType.toastr)));
    }
  }

  async function handleSubmit(user_id: string, display_name: string, department: string, user_number: number) {
    const is_web = AppConfiguration.buildForWeb();
    if (is_web) {
      dispatch(uiActions.handleRequestErrors(new Message(DeveloperWarningMessages.warning_wrong_port, MessageHandlerDisplayType.toastr)));
      return;
    }
    try {
      const prefix = user_id.split("@")[0] + "@";
      const domain = user_id.split("@")[1];
      const user_promises = [];
      for (let i = 1; i <= user_number; i++) {
        const new_user_id = prefix.replace(/@/, "+" + i.toString() + "@") + domain;
        const new_display_name = `${display_name} ${i}`;
        user_promises.push(
          addNewUsers(admin_id, org_id, new_user_id, new_display_name, "standard", department)
        );
      }
      await Promise.all(user_promises);
      setSuccess(true);
      dispatch(uiActions.handleSetMessage(new Message(DeveloperSuccessMessages.success_adding_user)));
    } catch (error: any) {
      handleError(error);
    }
  }

  return (
    <Formik
      validationSchema={schema}
      initialValues={{
        user_id: default_user_id,
        display_name: default_display_name,
        department: default_department,
        user_number: 1
      }}
      onSubmit={async (values, { setSubmitting }) => {
        await handleSubmit(values.user_id, values.display_name, values.department, values.user_number);
        setSubmitting(false);
      }}
    >
      {({ handleSubmit, handleChange, handleBlur, values, errors, touched, isSubmitting }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="user_id">User ID Prefix</Form.Label>
            <Form.Control
              id="user_id"
              name="user_id"
              type="email"
              placeholder={default_user_id}
              value={values.user_id}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.user_id && touched.user_id}
            />
            <Form.Control.Feedback type="invalid">
              {errors.user_id}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="display_name">Display Name Prefix</Form.Label>
            <Form.Control
              id="display_name"
              name="display_name"
              type="text"
              placeholder={default_display_name}
              value={values.display_name}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.display_name && touched.display_name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.display_name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="department">Department</Form.Label>
            <Form.Control
              id="department"
              name="department"
              type="text"
              placeholder="Dept"
              value={values.department}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.department && touched.department}
            />
            <Form.Control.Feedback type="invalid">
              {errors.department}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="user_number">Number of Users</Form.Label>
            <Form.Control
              id="user_number"
              name="user_number"
              type="number"
              min={1}
              max={200}
              placeholder="1"
              value={values.user_number}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.user_number && touched.user_number}
            />
            <Form.Control.Feedback type="invalid">
              {errors.user_number}
            </Form.Control.Feedback>
          </Form.Group>

          <Alert variant="info" className="mb-3">
            User IDs are created with a +1, +2, +3, etc. suffix to avoid conflicts. <br />
          </Alert>

          <Button type="submit" className="me-2" disabled={isSubmitting}>Create and Add Users</Button>
          {success && <Button variant="link" href={`/${PublicRoutes.choose_account_route}?${QueryParamKeys.PAGE_ACTION_QUERY_KEY}=${QueryParamValues.switch}`}>Choose Account</Button>}
        </Form>
      )}
    </Formik>
  );
}

export default React.memo(AddNewUserFormComponent); 
